import { useEffect, useRef } from 'react';
import { LeadService } from '../services';
import type {
  CheckoutLead,
  CheckoutLeadOnboardingStatus,
  CommercialLeadFilters,
  CommercialLeadListing,
} from '../types';

const POLL_INTERVAL_MS = 10000;

export function buildCommercialLeadFiltersKey(filters: CommercialLeadFilters): string {
  return [
    filters.searchName ?? '',
    (filters.classifications ?? []).join(','),
    (filters.leadTemperatureTypes ?? []).join(','),
    (filters.personaTypes ?? []).join(','),
    filters.hasPhotos === undefined ? '' : String(filters.hasPhotos),
    filters.hasParq === undefined ? '' : String(filters.hasParq),
    filters.hasAnamnesis === undefined ? '' : String(filters.hasAnamnesis),
    (filters.cancellationReasons ?? []).join(','),
    (filters.investmentInterests ?? []).join(','),
    (filters.startTimeExpectations ?? []).join(','),
    (filters.mainGoals ?? []).join(','),
    (filters.commercialStages ?? []).join(','),
    (filters.projectCodes ?? []).join(','),
    (filters.commercialOwnerIds ?? []).join(','),
    filters.joinedAtStart ?? '',
    filters.joinedAtEnd ?? '',
    filters.isInactive === undefined ? 'false' : String(filters.isInactive),
    filters.declinedRescue === true ? 'true' : 'false',
  ].join('#');
}

function hasOnboardingStatusChanged(
  lead: CheckoutLead,
  update: CheckoutLeadOnboardingStatus,
): boolean {
  const classificationChanged = lead.classification?.status !== update.classification?.status
    || lead.classification?.label !== update.classification?.label;

  return lead.has_paid !== update.has_paid
    || lead.paid_at !== update.paid_at
    || lead.has_photos !== update.has_photos
    || lead.has_parq !== update.has_parq
    || lead.has_anamnesis !== update.has_anamnesis
    || lead.parq_has_yes_answers !== update.parq_has_yes_answers
    || JSON.stringify(lead.parq_yes_answers) !== JSON.stringify(update.parq_yes_answers)
    || lead.selected_payment_method !== update.selected_payment_method
    || lead.student_subscription_id !== update.student_subscription_id
    || lead.commercial_stage !== update.commercial_stage
    || lead.commercial_owner_portal_user_id !== update.commercial_owner_portal_user_id
    || lead.commercial_owner_name !== update.commercial_owner_name
    || classificationChanged;
}

export function useCommercialLeadStatusPolling(
  leads: CheckoutLead[],
  filters: CommercialLeadFilters,
  checkoutLeadsCount: number,
  onStatusUpdate: (updates: Record<number, Partial<CheckoutLead>>) => void,
  onNewLeads: () => void,
  listing: CommercialLeadListing = 'commercial',
): void {
  const leadsRef = useRef(leads);
  const filtersRef = useRef(filters);
  const checkoutLeadsCountRef = useRef(checkoutLeadsCount);
  const onStatusUpdateRef = useRef(onStatusUpdate);
  const onNewLeadsRef = useRef(onNewLeads);

  leadsRef.current = leads;
  filtersRef.current = filters;
  checkoutLeadsCountRef.current = checkoutLeadsCount;
  onStatusUpdateRef.current = onStatusUpdate;
  onNewLeadsRef.current = onNewLeads;

  const filtersKey = buildCommercialLeadFiltersKey(filters);

  const pollKey = [
    listing,
    checkoutLeadsCount,
    leads
      .filter((lead) => lead.student_id != null && lead.student_id > 0)
      .map((lead) => `${lead.student_id}:${lead.student_subscription_id ?? ''}`)
      .join(','),
    filtersKey,
  ].join('#');

  useEffect(() => {
    let cancelled = false;

    const poll = async () => {
      const currentLeads = leadsRef.current;
      const pollableLeads = currentLeads.filter(
        (lead): lead is CheckoutLead & { student_id: number } => (
          lead.student_id != null && lead.student_id > 0
        ),
      );

      try {
        const result = await LeadService.pollCommercialLeadsStatus(
          pollableLeads.map((lead) => ({
            studentId: lead.student_id,
            studentSubscriptionId: lead.student_subscription_id,
            qualificationId: lead.qualification_id,
          })),
          checkoutLeadsCountRef.current,
          filtersRef.current,
          listing,
        );

        if (cancelled) {
          return;
        }

        if (result.hasNewLeads) {
          onNewLeadsRef.current();
          return;
        }

        if (pollableLeads.length === 0 || Object.keys(result.statuses).length === 0) {
          return;
        }

        const changedUpdates = Object.entries(result.statuses).reduce<
          Record<number, CheckoutLeadOnboardingStatus>
        >((accumulator, [studentId, update]) => {
          const lead = pollableLeads.find((item) => item.student_id === Number(studentId));
          if (lead && hasOnboardingStatusChanged(lead, update)) {
            accumulator[Number(studentId)] = update;
          }
          return accumulator;
        }, {});

        if (Object.keys(changedUpdates).length > 0) {
          onStatusUpdateRef.current(changedUpdates);
        }
      } catch {
        // Polling failures should not interrupt the commercial screen.
      }
    };

    let intervalId: number | undefined;

    const initialTimeoutId = window.setTimeout(() => {
      void poll();

      intervalId = window.setInterval(() => {
        void poll();
      }, POLL_INTERVAL_MS);
    }, POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      window.clearTimeout(initialTimeoutId);
      if (intervalId !== undefined) {
        window.clearInterval(intervalId);
      }
    };
  }, [listing, pollKey]);
}
